import { useState, useEffect, useCallback } from 'react';
import type { Plant } from '../types/plant';
import { subscribeToPlant, getPlant } from '../services/plants';

interface UsePlantResult {
  plant: Plant | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to subscribe to real-time plant updates
 */
export function usePlant(userId: string | null, plantId: string | null): UsePlantResult {
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!userId || !plantId) {
      setPlant(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToPlant(
      userId,
      plantId,
      (newPlant) => {
        setPlant(newPlant);
        setLoading(false);
        if (!newPlant) {
          setError('Plant not found');
        }
      },
      (err) => {
        setError(err.message || 'Failed to load plant');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, plantId, refreshKey]);

  return { plant, loading, error, refetch };
}

/**
 * Hook to fetch a plant once (no real-time updates)
 */
export function usePlantOnce(userId: string | null, plantId: string | null): UsePlantResult {
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!userId || !plantId) {
      setPlant(null);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    getPlant(userId, plantId)
      .then((result) => {
        if (mounted) {
          setPlant(result);
          setLoading(false);
          if (!result) {
            setError('Plant not found');
          }
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err.message || 'Failed to load plant');
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [userId, plantId, refreshKey]);

  return { plant, loading, error, refetch };
}
